import { useState } from "react";
import axios from "axios";
import { savePendingDebtPayment } from "../../../../utils/debtPaymentStorage";

const DEBT_BLOCK_FALLBACK =
  "Masz nieopłacone zajęcia, które już się odbyły. Opłać je, aby zapisać się na nowe.";

// Backend returns 402 in two cases:
//  - DEBT_BLOCK (legacy): detail is a plain string with the user-facing message
//  - NO_PASS_NO_PAYMENT: detail is an object { code, message, lesson_price }
const resolve402Message = (detail) => {
  if (detail && typeof detail === "object" && detail.message) {
    return detail.message;
  }
  if (typeof detail === "string" && detail) {
    return detail;
  }
  return DEBT_BLOCK_FALLBACK;
};

export const useSignUpLogic = (
  user,
  email,
  name,
  lastName,
  phone,
  isAuthenticated,
  addLesson,
  setNotification
) => {
  const [loading, setLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // Returns the signup response on success, `true` when the user landed on
  // (or already is on) the waitlist, false otherwise.
  // duoMode: "whole" | "spot" — only meaningful for duo/trio lessons.
  const handleUserToLesson = async (lessonData, formValues, fetchCanSignData, friendIds = [], duoMode = "whole") => {
    const idToken = await user.getIdToken();
    const req = {
      lesson_id: lessonData.lesson_id,
      email: isAuthenticated ? email : null,
      name: isAuthenticated ? name : null,
      lastName: isAuthenticated ? lastName : null,
      phone: isAuthenticated ? phone : null,
      pass_id: formValues.selectedPass,
      friend_ids: friendIds,
      duo_mode: duoMode,
    };

    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user_to_lesson/`,
        req,
        { headers: { Authorization: `Bearer ${idToken}` } },
      );

      if (resp.data.status_code === 200) {
        if (resp.data.status === "waitlisted") {
          setNotification({
            message: `Zajęcia są pełne. Zostałeś dodany do listy oczekujących na pozycji ${resp.data.waitlist_position}.`,
            type: "info",
          });
          // Refresh the can sign data to show waitlist status
          await fetchCanSignData();
          return true;
        } else if (resp.data.status === "already_waitlisted") {
          setNotification({
            message: `Jesteś już na liście oczekujących na pozycji ${resp.data.waitlist_position}.`,
            type: "info",
          });
          return true;
        } else {
          setNotification({
            message: friendIds.length ? "Zapisano Ciebie i znajomych na zajęcia!" : "Zapisano na zajęcia!",
            type: "success",
          });
          addLesson(lessonData);
          return resp.data;
        }
      }

      // handle errors
      if (resp.data.status_code === 404) {
        setNotification({
          message: "Niestety, maksymalna ilość osób na tych zajęciach została już osiągnięta :(",
          type: "error",
        });
      } else if (resp.data.status_code === 422) {
        setNotification({
          message: "Niestety, wybrany karnet nie będzie ważny na te zajęcia :(",
          type: "error",
        });
      } else if (resp.data.status_code === 402) {
        setNotification({
          message: resolve402Message(resp.data.detail),
          type: "error",
        });
        // Refresh can sign data to show debt information
        await fetchCanSignData();
      } else {
        setNotification({ message: "Nieznany błąd :(", type: "error" });
      }
    } catch (error) {
      if (error.response && error.response.status === 402) {
        setNotification({
          message: resolve402Message(error.response.data?.detail),
          type: "error",
        });
        // Refresh can sign data to show debt information
        await fetchCanSignData();
      } else if (typeof error.response?.data?.detail === "string" && (friendIds.length || lessonData?.lesson_type?.participants_per_booking)) {
        // Group / duo rules explain themselves (missing seats, friend's debt, ...).
        setNotification({ message: error.response.data.detail, type: "error" });
      } else {
        setNotification({ message: "Nieznany błąd :(", type: "error" });
      }
    }

    return false;
  };

  const handleSignOutLesson = async (lessonData) => {
    setSigningOut(true);
    const idToken = await user.getIdToken();

    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/sign_me_out/`,
        { lesson_id: lessonData.lesson_id },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );

      if (r.status === 200) {
        setNotification({ message: "Wypisano z zajęć!", type: "success" });
        setSigningOut(false);
        return true;
      }
      setNotification({ message: "Nieznany błąd :(", type: "error" });
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = "Nieznany błąd :(";
      if (detail === "24_hours_only") {
        message = "Wypisanie z zajęć jest możliwe tylko do 24h przed zajęciami.";
      } else if (typeof detail === "string") {
        message = detail;
      }
      setNotification({ message, type: "error" });
    }

    setSigningOut(false);
    return false;
  };

  const handleRemoveFromWaitlist = async (lessonData, fetchCanSignData) => {
    setLoading(true);
    const idToken = await user.getIdToken();

    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/remove_from_waitlist/`,
        { lesson_id: lessonData.lesson_id },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      if (resp.status === 200) {
        setNotification({ 
          message: "Usunięto z listy oczekujących!", 
          type: "success" 
        });
        await fetchCanSignData();
        setLoading(false);
        return true;
      } else {
        setNotification({
          message: "Nie udało się usunąć z listy oczekujących.",
          type: "error",
        });
      }
    } catch (error) {
      setNotification({
        message: "Nie udało się usunąć z listy oczekujących.",
        type: "error",
      });
    }

    setLoading(false);
    return false;
  };

  const handlePayment = async (lessonData) => {
    const idToken = await user.getIdToken();
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/payment/create_order`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ lesson_id: lessonData.lesson_id }),
        },
      );

      if (!resp.ok) throw new Error("Failed");
      window.location.href = await resp.json();
    } catch {
      window.alert("Nie udało się dokonać płatności.");
    }
  };

  const handlePayDebt = async (canSignData) => {
    const idToken = await user.getIdToken();
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/payment/create_debt_order`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            debt_lessons: canSignData.debt_details,
            total_amount: canSignData.debt_amount 
          }),
        },
      );

      if (!resp.ok) throw new Error("Failed");
      // Remember what is being paid — after the PayU redirect lands back,
      // DebtPaymentResultNotice confirms exactly which lessons got settled.
      savePendingDebtPayment(canSignData.debt_details || []);
      window.location.href = await resp.json();
    } catch {
      window.alert("Nie udało się dokonać płatności za dług.");
    }
  };

  return {
    loading,
    setLoading,
    signingOut,
    handleUserToLesson,
    handleSignOutLesson,
    handleRemoveFromWaitlist,
    handlePayment,
    handlePayDebt
  };
};
